import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { ServerListQuery } from '@/types/fleet';

/** Multi-value filter keys — each is stored as repeated query params. */
export const MULTI_FILTER_KEYS = [
  'role',
  'status',
  'provider',
  'region_continent',
  'region_country',
  'protocol',
  'capability',
] as const;

export type MultiFilterKey = (typeof MULTI_FILTER_KEYS)[number];

const DEFAULT_PAGE_SIZE = 50;

export interface FleetFilterControls {
  query: ServerListQuery;
  /** Replace the values of one multi-select filter. */
  setMulti: (key: MultiFilterKey, values: string[]) => void;
  setSearch: (q: string) => void;
  setSort: (sort: string) => void;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  reset: () => void;
  /** True when any filter (other than pagination/sort) is active. */
  isFiltered: boolean;
}

/**
 * Single source of truth for the /fleet filter + pagination + sort state.
 * Everything lives in the URL query string so links are shareable.
 */
export function useFleetFilters(): FleetFilterControls {
  const [params, setParams] = useSearchParams();

  const query = useMemo<ServerListQuery>(() => {
    const result: ServerListQuery = {};

    for (const key of MULTI_FILTER_KEYS) {
      const values = params.getAll(key);

      if (values.length > 0) result[key] = values;
    }

    const q = params.get('q');

    if (q) result.q = q;

    const sort = params.get('sort');

    if (sort) result.sort = sort;

    const page = Number(params.get('page'));

    result.page = Number.isInteger(page) && page > 0 ? page : 1;

    const pageSize = Number(params.get('page_size'));

    result.page_size =
      Number.isInteger(pageSize) && pageSize > 0 ? pageSize : DEFAULT_PAGE_SIZE;

    return result;
  }, [params]);

  const update = useCallback(
    (mutate: (next: URLSearchParams) => void) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);

          mutate(next);

          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const setMulti = useCallback(
    (key: MultiFilterKey, values: string[]) => {
      update((next) => {
        next.delete(key);
        for (const value of values) next.append(key, value);
        next.delete('page'); // filter change resets to page 1
      });
    },
    [update],
  );

  const setSearch = useCallback(
    (q: string) => {
      update((next) => {
        if (q) next.set('q', q);
        else next.delete('q');
        next.delete('page');
      });
    },
    [update],
  );

  const setSort = useCallback(
    (sort: string) => {
      update((next) => {
        if (sort) next.set('sort', sort);
        else next.delete('sort');
      });
    },
    [update],
  );

  const setPage = useCallback(
    (page: number) => {
      update((next) => {
        if (page > 1) next.set('page', String(page));
        else next.delete('page');
      });
    },
    [update],
  );

  const setPageSize = useCallback(
    (size: number) => {
      update((next) => {
        next.set('page_size', String(size));
        next.delete('page');
      });
    },
    [update],
  );

  const reset = useCallback(() => {
    setParams({}, { replace: true });
  }, [setParams]);

  const isFiltered = useMemo(
    () => MULTI_FILTER_KEYS.some((key) => params.getAll(key).length > 0) || Boolean(params.get('q')),
    [params],
  );

  return { query, setMulti, setSearch, setSort, setPage, setPageSize, reset, isFiltered };
}
